import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Spinner } from '@/shared/components/Feedback/Spinner';
import { AppLayout } from '@/shared/components/Layout/AppLayout';
import { AgencyAppLayout } from '@/shared/components/Layout/AgencyAppLayout';
import { IS_KEYCLOAK_MODE } from '@/features/auth/auth.config';
import useAuthStore from '@/app/store/auth.store';
import { useAuth } from '@/shared/hooks/useAuth';

const AgencyDashboardPage = lazy(() => import('@/features/dashboard/AgencyDashboardPage'));
const InboxPage = lazy(() => import('@/features/inbox/InboxPage'));
const LoginPage = lazy(() => import('@/features/auth/LoginPage'));

// Self-service pages (Phase 2)
const LeavePage = lazy(() => import('@/features/my/leave/LeavePage'));
const LeaveDetailPage = lazy(() => import('@/features/my/leave/LeaveDetailPage'));
const ExpensesPage = lazy(() => import('@/features/my/expenses/ExpensesPage'));
const ExpenseDetailPage = lazy(() => import('@/features/my/expenses/ExpenseDetailPage'));
const TimesheetsPage = lazy(() => import('@/features/my/timesheets/TimesheetsPage'));
const TimesheetDetailPage = lazy(() => import('@/features/my/timesheets/TimesheetDetailPage'));
const PayslipsPage = lazy(() => import('@/features/my/payslips/PayslipsPage'));
const PayslipDetailPage = lazy(() => import('@/features/my/payslips/PayslipDetailPage'));
const DocumentsPage = lazy(() => import('@/features/my/documents/DocumentsPage'));
const DocumentDetailPage = lazy(() => import('@/features/my/documents/DocumentDetailPage'));
const MissionPage = lazy(() => import('@/features/my/mission/MissionPage'));

// Team manager pages (Phase 3)
const TeamDashboardPage = lazy(() => import('@/features/team/TeamDashboardPage'));
const TeamLeavePage = lazy(() => import('@/features/team/leave/TeamLeavePage'));
const TeamLeaveDetailPage = lazy(() => import('@/features/team/leave/TeamLeaveDetailPage'));
const TeamTimesheetsPage = lazy(() => import('@/features/team/timesheets/TeamTimesheetsPage'));
const TeamTimesheetDetailPage = lazy(() => import('@/features/team/timesheets/TeamTimesheetDetailPage'));
const TeamExpensesPage = lazy(() => import('@/features/team/expenses/TeamExpensesPage'));
const TeamExpenseDetailPage = lazy(() => import('@/features/team/expenses/TeamExpenseDetailPage'));
const TeamMissionsPage = lazy(() => import('@/features/team/missions/TeamMissionsPage'));
const TeamMissionDetailPage = lazy(() => import('@/features/team/missions/TeamMissionDetailPage'));
const TeamClientsPage = lazy(() => import('@/features/team/clients/TeamClientsPage'));

// HR Admin pages (Phase 4)
const EmployeesPage = lazy(() => import('@/features/hr/employees/EmployeesPage'));
const EmployeeDetailPage = lazy(() => import('@/features/hr/employees/EmployeeDetailPage'));
const ContractsPage = lazy(() => import('@/features/hr/contracts/ContractsPage'));
const DepartmentsPage = lazy(() => import('@/features/hr/departments/DepartmentsPage'));
const PositionsPage = lazy(() => import('@/features/hr/positions/PositionsPage'));
const PayrollPage = lazy(() => import('@/features/hr/payroll/PayrollPage'));
const PayrollPeriodDetailPage = lazy(() => import('@/features/hr/payroll/PayrollPeriodDetailPage'));
const PayrollConfigPage = lazy(() => import('@/features/hr/payroll/PayrollConfigPage'));

// HR Admin - Planning pages
const PlanningPage = lazy(() => import('@/features/hr/planning/PlanningPage'));
const PlanningDetailPage = lazy(() => import('@/features/hr/planning/PlanningDetailPage'));

// HR Admin - Leave management pages
const LeaveTypesPage = lazy(() => import('@/features/hr/leave-config/LeaveTypesPage'));
const LeavePoliciesPage = lazy(() => import('@/features/hr/leave-config/LeavePoliciesPage'));
const PublicHolidaysPage = lazy(() => import('@/features/hr/leave-config/PublicHolidaysPage'));
const LeaveBalancesPage = lazy(() => import('@/features/hr/leave-balances/LeaveBalancesPage'));

// HR Admin - Interim agency pages (Phase 6)
const MissionsAdminPage = lazy(() => import('@/features/hr/admin/missions/MissionsAdminPage'));
const CreateMissionWizardPage = lazy(() => import('@/features/hr/admin/missions/CreateMissionWizardPage'));
const MissionDetailPage = lazy(() => import('@/features/hr/admin/missions/MissionDetailPage'));
const MissionsExpiringPage = lazy(() => import('@/features/hr/admin/missions/MissionsExpiringPage'));
const MissionMarginsPage = lazy(() => import('@/features/hr/admin/missions/MissionMarginsPage'));
const PeopleAnalyticsPage = lazy(() => import('@/features/hr/analytics/PeopleAnalyticsPage'));
const ComplianceAlertsPage = lazy(() => import('@/features/hr/compliance/ComplianceAlertsPage'));
const CarenceAlertsPage = lazy(() => import('@/features/hr/compliance/CarenceAlertsPage'));
const ExitInterviewsPage = lazy(() => import('@/features/hr/exit-interviews/ExitInterviewsPage'));
const OnboardingPage = lazy(() => import('@/features/hr/onboarding/OnboardingPage'));
const MissionFeedbacksPage = lazy(() => import('@/features/hr/mission-feedbacks/MissionFeedbacksPage'));
const AnnualReviewsPage = lazy(() => import('@/features/hr/annual-reviews/AnnualReviewsPage'));
const TrainingsPage = lazy(() => import('@/features/hr/trainings/TrainingsPage'));
const SkillsPage = lazy(() => import('@/features/hr/skills/SkillsPage'));
// HR Admin - Recruitment (vivier candidats + CVs)
const CandidatesListPage = lazy(() => import('@/features/hr/recruitment/pages/CandidatesListPage'));
const CandidateCreatePage = lazy(() => import('@/features/hr/recruitment/pages/CandidateCreatePage'));
const CandidateDetailPage = lazy(() => import('@/features/hr/recruitment/pages/CandidateDetailPage'));
const RgpdAdminPage = lazy(() => import('@/features/hr/recruitment/pages/RgpdAdminPage'));
// HR Admin - Job Posts (annonces)
const JobPostsListPage   = lazy(() => import('@/features/hr/recruitment/jobposts/pages/JobPostsListPage'));
const JobPostCreatePage  = lazy(() => import('@/features/hr/recruitment/jobposts/pages/JobPostCreatePage'));
const JobPostEditPage    = lazy(() => import('@/features/hr/recruitment/jobposts/pages/JobPostEditPage'));
const JobPostDetailPage  = lazy(() => import('@/features/hr/recruitment/jobposts/pages/JobPostDetailPage'));
const JobPostPipelinePage = lazy(() => import('@/features/hr/recruitment/jobposts/pipeline/pages/JobPostPipelinePage'));
const ApplicationDetailPage = lazy(() => import('@/features/hr/recruitment/jobposts/pipeline/pages/ApplicationDetailPage'));
// Public recruitment pages (no auth required)
const PublicJobsListPage = lazy(() => import('@/features/recruitment-public/pages/PublicJobsListPage'));
const PublicJobDetailPage = lazy(() => import('@/features/recruitment-public/pages/PublicJobDetailPage'));
const ClientsAdminPage = lazy(() => import('@/features/hr/admin/clients/ClientsAdminPage'));
const ClientDetailPage = lazy(() => import('@/features/hr/admin/clients/ClientDetailPage'));
const InterimairesPage = lazy(() => import('@/features/hr/admin/interimaires/InterimairesPage'));
const InterimaireDetailPage = lazy(() => import('@/features/hr/admin/interimaires/InterimaireDetailPage'));
const InterimMatchingPage = lazy(() => import('@/features/hr/admin/interimaires/InterimMatchingPage'));
const AdminTimesheetsPage = lazy(() => import('@/features/hr/admin/timesheets/AdminTimesheetsPage'));

// Admin module pages
const UsersPage = lazy(() => import('@/features/admin/users/UsersPage'));
const UserDetailPage = lazy(() => import('@/features/admin/users/UserDetailPage'));
const TenantsPage = lazy(() => import('@/features/admin/tenants/TenantsPage'));
const TenantDetailPage = lazy(() => import('@/features/admin/tenants/TenantDetailPage'));
const ProfilesPage = lazy(() => import('@/features/admin/profiles/ProfilesPage'));
const ProfileDetailPage = lazy(() => import('@/features/admin/profiles/ProfileDetailPage'));
const PermissionsPage = lazy(() => import('@/features/admin/permissions/PermissionsPage'));
const ModulesPage = lazy(() => import('@/features/admin/modules/ModulesPage'));
const LicencesPage = lazy(() => import('@/features/admin/licences/LicencesPage'));
const KeycloakSyncPage = lazy(() => import('@/features/admin/keycloak/KeycloakSyncPage'));
const RoleMappingsPage = lazy(() => import('@/features/admin/keycloak/RoleMappingsPage'));
const WorkflowsAdminPage = lazy(() => import('@/features/admin/workflows/WorkflowsAdminPage'));
const ImportsAdminPage = lazy(() => import('@/features/admin/imports/ImportsAdminPage'));

// Admin pages (Phase 5)
const PaymentDashboardPage = lazy(() => import('@/features/payments/dashboard/DashboardPage'));
const PaymentTasksPage = lazy(() => import('@/features/payments/tasks/TasksPage'));
const TransferWizardPage = lazy(() => import('@/features/payments/transfer/TransferWizardPage'));
const PaymentAccountsPage = lazy(() => import('@/features/payments/accounts/AccountsPage'));
const BeneficiariesPage = lazy(() => import('@/features/payments/beneficiaries/BeneficiariesPage'));
const CampaignsPage = lazy(() => import('@/features/communication/campaigns/CampaignsPage'));
const TemplatesPage = lazy(() => import('@/features/communication/templates/TemplatesPage'));
const ContactListsPage = lazy(() => import('@/features/communication/contacts/ContactListsPage'));
const ProvidersPage = lazy(() => import('@/features/communication/providers/ProvidersPage'));
const GedDocumentsPage = lazy(() => import('@/features/ged/documents/GedDocumentsPage'));
const CategoriesPage = lazy(() => import('@/features/ged/categories/CategoriesPage'));
const InvoicesPage = lazy(() => import('@/features/billing/invoices/InvoicesPage'));
const InvoiceDetailPage = lazy(() => import('@/features/billing/invoices/InvoiceDetailPage'));
const ConfigurationsPage = lazy(() => import('@/features/workflows/configurations/ConfigurationsPage'));
const ConfigurationDetailPage = lazy(() => import('@/features/workflows/configurations/ConfigurationDetailPage'));
const InstancesPage = lazy(() => import('@/features/workflows/instances/InstancesPage'));
const InstanceDetailPage = lazy(() => import('@/features/workflows/instances/InstanceDetailPage'));

const AGENCY_PREFIXES = ['/inbox', '/hr/admin', '/hr/employees', '/hr/contracts', '/hr/payroll', '/hr/planning', '/hr/leave-balances', '/hr/leave-config', '/hr/positions', '/hr/departments', '/billing', '/payments', '/workflows', '/ged', '/admin', '/hr/missions', '/hr/missions-ending', '/hr/analytics', '/hr/compliance', '/hr/exit-interviews', '/hr/onboarding', '/hr/annual-reviews', '/hr/trainings', '/hr/skills', '/hr/recruitment', '/hr/mission-feedbacks', '/hr/clients', '/hr/interimaires', '/hr/leave-types', '/hr/leave-policies', '/hr/public-holidays', '/communication', '/dashboard', '/hr/timesheets', '/team'];

function isAgencyPath(path: string): boolean {
  if (path === '/') return true;
  return AGENCY_PREFIXES.some((p) => path.startsWith(p));
}

const LayoutSwitch: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  return isAgencyPath(pathname) ? <AgencyAppLayout>{children}</AgencyAppLayout> : <AppLayout>{children}</AppLayout>;
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (!IS_KEYCLOAK_MODE) return <>{children}</>;
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { adminContext } = useAuth();
  const isAdmin = adminContext?.isAdmin || adminContext?.canManageUsers || adminContext?.canManageTenants || adminContext?.canManagePermissions;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

export const AppRouter: React.FC = () => (
  <Suspense fallback={<div className="flex items-center justify-center h-screen"><Spinner size="lg" /></div>}>
    <Routes>
      {IS_KEYCLOAK_MODE && <Route path="/login" element={<LoginPage />} />}
      <Route path="/jobs/:tenantId" element={
        <Suspense fallback={<div className="flex items-center justify-center h-screen"><Spinner size="lg" /></div>}><PublicJobsListPage /></Suspense>
      } />
      <Route path="/jobs/:tenantId/:slug" element={
        <Suspense fallback={<div className="flex items-center justify-center h-screen"><Spinner size="lg" /></div>}><PublicJobDetailPage /></Suspense>
      } />
      <Route path="/*" element={
        <ProtectedRoute>
          <LayoutSwitch>
            <Suspense fallback={<div className="flex items-center justify-center h-full"><Spinner size="lg" /></div>}>
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<AgencyDashboardPage />} />
                <Route path="/inbox" element={<InboxPage />} />
                <Route path="/my/leave" element={<LeavePage />} />
                <Route path="/my/leave/:id" element={<LeaveDetailPage />} />
                <Route path="/my/expenses" element={<ExpensesPage />} />
                <Route path="/my/expenses/:id" element={<ExpenseDetailPage />} />
                <Route path="/my/timesheets" element={<TimesheetsPage />} />
                <Route path="/my/timesheets/:id" element={<TimesheetDetailPage />} />
                <Route path="/my/payslips" element={<PayslipsPage />} />
                <Route path="/my/payslips/:id" element={<PayslipDetailPage />} />
                <Route path="/my/documents" element={<DocumentsPage />} />
                <Route path="/my/documents/:id" element={<DocumentDetailPage />} />
                <Route path="/my/mission" element={<MissionPage />} />
                <Route path="/team" element={<TeamDashboardPage />} />
                <Route path="/team/leave" element={<TeamLeavePage />} />
                <Route path="/team/leave/:id" element={<TeamLeaveDetailPage />} />
                <Route path="/team/timesheets" element={<TeamTimesheetsPage />} />
                <Route path="/team/timesheets/:id" element={<TeamTimesheetDetailPage />} />
                <Route path="/team/expenses" element={<TeamExpensesPage />} />
                <Route path="/team/expenses/:id" element={<TeamExpenseDetailPage />} />
                <Route path="/team/missions" element={<TeamMissionsPage />} />
                <Route path="/team/missions/:id" element={<TeamMissionDetailPage />} />
                <Route path="/team/clients" element={<TeamClientsPage />} />
                <Route path="/hr/employees" element={<EmployeesPage />} />
                <Route path="/hr/employees/:id" element={<EmployeeDetailPage />} />
                <Route path="/hr/contracts" element={<ContractsPage />} />
                <Route path="/hr/departments" element={<DepartmentsPage />} />
                <Route path="/hr/positions" element={<PositionsPage />} />
                <Route path="/hr/payroll" element={<PayrollPage />} />
                <Route path="/hr/payroll/config" element={<PayrollConfigPage />} />
                <Route path="/hr/payroll/:id" element={<PayrollPeriodDetailPage />} />
                <Route path="/hr/planning" element={<PlanningPage />} />
                <Route path="/hr/planning/:id" element={<PlanningDetailPage />} />
                <Route path="/hr/leave-types" element={<LeaveTypesPage />} />
                <Route path="/hr/leave-policies" element={<LeavePoliciesPage />} />
                <Route path="/hr/public-holidays" element={<PublicHolidaysPage />} />
                <Route path="/hr/leave-balances" element={<LeaveBalancesPage />} />
                <Route path="/hr/missions" element={<MissionsAdminPage />} />
                <Route path="/hr/missions-ending" element={<MissionsExpiringPage />} />
                <Route path="/hr/missions/margins" element={<MissionMarginsPage />} />
                <Route path="/hr/analytics" element={<PeopleAnalyticsPage />} />
                <Route path="/hr/compliance/documents" element={<ComplianceAlertsPage />} />
                <Route path="/hr/compliance/carence" element={<CarenceAlertsPage />} />
                <Route path="/hr/exit-interviews" element={<ExitInterviewsPage />} />
                <Route path="/hr/onboarding" element={<OnboardingPage />} />
                <Route path="/hr/mission-feedbacks" element={<MissionFeedbacksPage />} />
                <Route path="/hr/annual-reviews" element={<AnnualReviewsPage />} />
                <Route path="/hr/trainings" element={<TrainingsPage />} />
                <Route path="/hr/skills" element={<SkillsPage />} />
<Route path="/hr/recruitment" element={<CandidatesListPage />} />
<Route path="/hr/recruitment/new" element={<CandidateCreatePage />} />
<Route path="/hr/recruitment/rgpd" element={<RgpdAdminPage />} />
<Route path="/hr/recruitment/job-posts" element={<JobPostsListPage />} />
<Route path="/hr/recruitment/job-posts/new" element={<JobPostCreatePage />} />
<Route path="/hr/recruitment/job-posts/:publicId" element={<JobPostDetailPage />} />
<Route path="/hr/recruitment/job-posts/:publicId/edit" element={<JobPostEditPage />} />
<Route path="/hr/recruitment/job-posts/:publicId/pipeline" element={<JobPostPipelinePage />} />
<Route path="/hr/recruitment/applications/:appPublicId" element={<ApplicationDetailPage />} />
<Route path="/hr/recruitment/:id" element={<CandidateDetailPage />} />
                <Route path="/hr/missions/new" element={<CreateMissionWizardPage />} />
                <Route path="/hr/missions/:id" element={<MissionDetailPage />} />
                <Route path="/hr/clients" element={<ClientsAdminPage />} />
                <Route path="/hr/clients/:id" element={<ClientDetailPage />} />
                <Route path="/hr/interimaires" element={<InterimairesPage />} />
                <Route path="/hr/interimaires/matching" element={<InterimMatchingPage />} />
                <Route path="/hr/interimaires/:id" element={<InterimaireDetailPage />} />
                <Route path="/hr/timesheets" element={<AdminTimesheetsPage />} />
                <Route path="/payments" element={<PaymentDashboardPage />} />
                <Route path="/payments/tasks" element={<PaymentTasksPage />} />
                <Route path="/payments/transfer" element={<TransferWizardPage />} />
                <Route path="/payments/accounts" element={<PaymentAccountsPage />} />
                <Route path="/payments/beneficiaries" element={<BeneficiariesPage />} />
                <Route path="/communication/campaigns" element={<CampaignsPage />} />
                <Route path="/communication/templates" element={<TemplatesPage />} />
                <Route path="/communication/contacts" element={<ContactListsPage />} />
                <Route path="/communication/providers" element={<ProvidersPage />} />
                <Route path="/ged/documents" element={<GedDocumentsPage />} />
                <Route path="/ged/categories" element={<CategoriesPage />} />
                <Route path="/billing/invoices" element={<InvoicesPage />} />
                <Route path="/billing/invoices/:id" element={<InvoiceDetailPage />} />
                <Route path="/workflows/configurations/new" element={<ConfigurationDetailPage />} />
                <Route path="/workflows/configurations/:id" element={<ConfigurationDetailPage />} />
                <Route path="/workflows/configurations" element={<ConfigurationsPage />} />
                <Route path="/workflows/instances" element={<InstancesPage />} />
                <Route path="/workflows/instances/:id" element={<InstanceDetailPage />} />
                <Route path="/admin/users" element={<AdminRoute><UsersPage /></AdminRoute>} />
                <Route path="/admin/users/:id" element={<AdminRoute><UserDetailPage /></AdminRoute>} />
                <Route path="/admin/tenants" element={<AdminRoute><TenantsPage /></AdminRoute>} />
                <Route path="/admin/tenants/:id" element={<AdminRoute><TenantDetailPage /></AdminRoute>} />
                <Route path="/admin/profiles" element={<AdminRoute><ProfilesPage /></AdminRoute>} />
                <Route path="/admin/profiles/:id" element={<AdminRoute><ProfileDetailPage /></AdminRoute>} />
                <Route path="/admin/permissions" element={<AdminRoute><PermissionsPage /></AdminRoute>} />
                <Route path="/admin/modules" element={<AdminRoute><ModulesPage /></AdminRoute>} />
                <Route path="/admin/licences" element={<AdminRoute><LicencesPage /></AdminRoute>} />
                <Route path="/admin/keycloak" element={<AdminRoute><KeycloakSyncPage /></AdminRoute>} />
                <Route path="/admin/keycloak/mappings" element={<AdminRoute><RoleMappingsPage /></AdminRoute>} />
                <Route path="/admin/workflows" element={<AdminRoute><WorkflowsAdminPage /></AdminRoute>} />
                <Route path="/admin/imports" element={<AdminRoute><ImportsAdminPage /></AdminRoute>} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Suspense>
          </LayoutSwitch>
        </ProtectedRoute>
      } />
    </Routes>
  </Suspense>
);
